import { encryptionPhone } from '@flowus/common';
import { RoleLevel } from '@flowus/common/block/type';
import { cx } from '@flowus/common/cx';
import { toDayjsDateFormat } from '@flowus/common/i18n';
import { PermissionRole } from '@next-space/fe-api-idl';
import dayjs from 'dayjs';
import { exportCsv } from '@flowus/common/utils/export-file';
import type { FC, MouseEvent } from 'react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAppTranslation } from '@flowus/common/i18n/use-translation';
import { useHistory } from 'react-router-dom';
import { Button } from 'src/common/components/button';
import { Icon } from 'src/common/components/icon';
import { Input } from 'src/common/components/input';
import { ListItemType, ListView } from 'src/common/components/list-view';
import { message } from 'src/common/components/message';
import { useOpenModal } from 'src/common/components/next-modal';
import { getUnnamedUser } from 'src/common/const';
import { useModel } from 'src/common/create-model';
import { request } from 'src/common/request';
import { downloadUrlFile } from 'src/common/utils/download-utils';
import { temporaryBatchAndUpdateUsers } from 'src/components/share/search-user/utils';
import { UserAvatar } from 'src/components/user-avatar';
import { useFetchRootPages } from 'src/hooks/page';
import {
  fetchSpaces,
  jumpAfterFetchSpaces,
  useCurrentSpace,
  useDeleteSpace,
} from 'src/hooks/space';
import { useCurrentSpaceUsers } from 'src/hooks/space/use-current-space-users';
import { useGetSpaceRolePermission } from 'src/hooks/space/use-get-space-role-permission';
import { useIsProSpace } from 'src/hooks/space/use-is-pro-space';
import { useSearchSpaceMembers } from 'src/hooks/space/use-search-space-members';
import { useShareSpaceLink } from 'src/hooks/space/use-share-space-link';
import { useCurrentUser } from 'src/hooks/user';
import { ADD_MARK_NAME } from 'src/redux/actions';
import { dispatch } from 'src/redux/store';
import { useLimitConfig } from 'src/services/app/hook/subscription-data';
import { fetchCurrentShareInfo, useCurrentSpacePlanInfo } from 'src/services/capacity/space-plans';
import { ViewPath } from 'src/utils';
import { getSpacePlanTypeName } from 'src/utils/block-utils';
import { writeTextInClipboard } from 'src/utils/clipboard';
import { SettingModalCommon, SettingProvider } from '../common';
import { OpenSettingFrom } from '../type';
import { useCloseSettingModal, useOpenUpgradeSpace } from '../use-open-setting-modal';
import { FoldItemList } from './fold-item-list';
import { ImportMember } from './import-member';
import { MarkNameEditButton } from './mark-name-edit-button';

export const Members: FC = () => {
  const { t } = useAppTranslation();
  const { spaceShareInfo } = useCurrentSpacePlanInfo();
  const { memberMaxLimitDesc, membersMaxLimit } = useLimitConfig();
  const closeSettingModal = useCloseSettingModal();
  const isProSpace = useIsProSpace();
  const users = useCurrentSpaceUsers();
  const fetchSpaceRootPages = useFetchRootPages();
  const spaceUsers = Object.values(users);
  const openModal = useOpenModal();
  const currentSpace = useCurrentSpace();
  const currentUser = useCurrentUser();
  const getSpaceRolePermission = useGetSpaceRolePermission();
  const deleteSpace = useDeleteSpace();
  const spaceEditor = getSpaceRolePermission(currentUser.uuid).editor;
  const history = useHistory();
  const space = useCurrentSpace();
  const openUpgradeSpace = useOpenUpgradeSpace();
  const searchSpaceMembers = useSearchSpaceMembers();
  const [searchKeywords, setSearchKeywords] = useState('');
  /** 点击升级空间 */
  const { checkoutUpgraded } = useModel(SettingProvider);
  // 有editorMarkNameId的时候表示正在编辑备注
  const [editorMarkNameId, setEditorMarkNameId] = useState<string>();
  // 记录一下备注名，blur的时候保存
  const lastEditMarkNameRef = useRef<string>();

  const userList = useMemo(() => {
    const result = searchSpaceMembers({ keywords: searchKeywords });

    result.sort((a, b) => {
      if (a.spaceRole && b.spaceRole) {
        return RoleLevel[b.spaceRole] - RoleLevel[a.spaceRole];
      }
      return 0;
    });
    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchKeywords, searchSpaceMembers, editorMarkNameId]);

  const spaceShareLink = useShareSpaceLink();

  // 除了这个 uuid 之外，还有没有其他管理员
  const getIsLastEditorUser = (uuid: string) => {
    return spaceUsers.some(
      (user) => user.spaceRole === PermissionRole.EDITOR && user.uuid !== uuid
    );
  };

  /** 获取邀请链接 */
  useEffect(() => {
    void fetchCurrentShareInfo();
  }, [spaceEditor]);

  /** 重置邀请链接 */
  const resetSpareShareLink = async () => {
    openModal.warning({
      title: t('views.settings.members.reset_confirm_title'),
      content: t('views.settings.members.reset_confirm_content'),
      confirm: async () => {
        const res = await request.infra.resetSpaceInvitation(currentSpace.uuid);
        message.success(t('views.settings.members.reset_success'));
        void fetchCurrentShareInfo({
          inviterId: res.uuid,
          createdAt: res.createdAt,
          expirationAt: res.expirationAt,
        });
      },
    });
  };

  const openMemberCapacityDialog = async () => {
    if (__PRIVATE__) {
      openModal.warning({
        title: t('views.settings.members.capacity_limit_title'),
        content: t('views.settings.members.capacity_limit_private'),
      });
      return;
    }
    openModal.warning({
      title: t('views.settings.members.capacity_limit_title'),
      content: [
        t('views.settings.members.capacity_limit_prefix'),
        getSpacePlanTypeName(currentSpace.planType),
        t('views.settings.members.capacity_limit_suffix'),
      ],
      confirm: async () => {
        openUpgradeSpace(OpenSettingFrom.importMember);
      },
    });
  };

  /** 点击复制链接 */
  const clickCopy = () => {
    void writeTextInClipboard(spaceShareLink, { message: 'none' });
    message.success(t('views.settings.members.copy_success'));
  };

  /** 删除成员 */
  const deleteMember = (uuid: string) => {
    const isMe = uuid === currentUser.uuid;

    // 如果我要离开，并且自己是唯一的管理员，则只能删除空间
    if (isMe && spaceEditor && !getIsLastEditorUser(uuid)) {
      if (isProSpace) {
        openModal.warning({
          title: t('views.settings.members.cannot_leave_pro_space'),
          confirmText: t('common.got_it'),
        });
        return;
      }
      openModal.warning({
        title: t('views.settings.members.delete_space_confirm'),
        content: t('views.settings.members.delete_space_content'),
        countTime: 10,
        confirmText: t('common.confirm'),
        confirm: async () => {
          await deleteSpace(currentSpace.uuid);
          closeSettingModal();
          if (!jumpAfterFetchSpaces()) {
            history.push(ViewPath.main);
          }
        },
      });
      return;
    }

    openModal.warning({
      title: isMe
        ? t('views.settings.members.leave_confirm')
        : t('views.settings.members.remove_confirm'),
      content: isMe
        ? t('views.settings.members.leave_content')
        : t('views.settings.members.remove_content'),
      confirmText: isMe ? t('common.confirm_leave') : t('views.settings.members.confirm_remove'),
      confirm: async () => {
        await request.infra.removeSpaceMembers(currentSpace.uuid, {
          userId: uuid,
        });
        if (isMe) {
          message.success(t('views.settings.members.left_space'));
          await fetchSpaces(true);
          if (!jumpAfterFetchSpaces()) {
            history.push(ViewPath.main);
          }
        } else {
          await fetchSpaceRootPages(currentSpace.uuid);
        }
      },
    });
  };

  /** 修改成员权限 */
  const changeUserRol = async (uuid: string, type: PermissionRole) => {
    const isMe = uuid === currentUser.uuid;
    if (isMe) {
      message.error(t('views.settings.members.cannot_change_own_role'));
      return;
    }
    await request.infra.setSpaceMemberRole(currentSpace.uuid, {
      userId: uuid,
      role: type,
    });

    void fetchSpaceRootPages(currentSpace.uuid);
    message.success(t('views.settings.members.change_role_success'));
  };

  /** 用户权限修改弹窗 */
  const openOption = (event: MouseEvent<HTMLDivElement>, uuid: string) => {
    const isMe = uuid === currentUser.uuid;
    const clickUser = spaceUsers.find((user) => user.uuid === uuid);
    if (!clickUser) return;

    openModal.dropdown({
      placement: 'bottom',
      offset: [0, 4],
      popcorn: event.currentTarget,
      content: ({ onCloseModal }) => (
        <ListView
          className="py-2 w-[270px] next-modal"
          onItemClick={() => onCloseModal()}
          items={
            spaceEditor
              ? [
                  {
                    type: ListItemType.TEXT_DESCRIPTION,
                    data: {
                      title: t('common.admin'),
                      description: t('views.settings.members.admin_description'),
                      selected: clickUser.spaceRole === PermissionRole.EDITOR,
                      onClick: () => void changeUserRol(uuid, PermissionRole.EDITOR),
                    },
                  },
                  {
                    type: ListItemType.TEXT_DESCRIPTION,
                    data: {
                      title: t('common.member'),
                      selected: clickUser.spaceRole !== PermissionRole.EDITOR,
                      onClick: () => void changeUserRol(uuid, PermissionRole.WRITER),
                    },
                  },
                  { type: ListItemType.LINE },
                  {
                    type: ListItemType.TEXT_DESCRIPTION,
                    data: {
                      title: (
                        <div className="text-red">
                          {isMe
                            ? t('views.settings.members.leave_team')
                            : t('views.settings.members.remove_team')}
                        </div>
                      ),
                      onClick: () => deleteMember(uuid),
                    },
                  },
                ]
              : [
                  {
                    type: ListItemType.TEXT_DESCRIPTION,
                    data: {
                      title: (
                        <div className="text-red">{t('views.settings.members.leave_team')}</div>
                      ),
                      onClick: () => deleteMember(uuid),
                    },
                  },
                ]
          }
        />
      ),
    });
  };

  const openImportMember = (e: MouseEvent) => {
    openModal.dropdown({
      popcorn: e.currentTarget,
      placement: 'left',
      content: ({ onCloseModal }) => {
        return (
          <ImportMember
            isGuest={false}
            onReceiveGuestList={async (list) => {
              if (membersMaxLimit && spaceUsers.length + list.length > membersMaxLimit) {
                void openMemberCapacityDialog();
                return;
              }
              const { userIds, markNames } = await temporaryBatchAndUpdateUsers(list);
              await request.infra.setSpaceMemberRoleBatch(space.uuid, userIds);
              if (Object.entries(markNames).length > 0) {
                await request.infra.addSpaceUserRemark(space.uuid, markNames);
                await fetchSpaceRootPages(space.uuid);
                message.success(t('views.settings.members.import_success'));
                onCloseModal();
              }
            }}
          />
        );
      },
    });
  };

  const openExportSpaceMember = async () => {
    const fileContent = userList.map((item) => {
      return [item.nickname, item.email || item.phone];
    });
    const url = exportCsv(fileContent);
    await downloadUrlFile(url);
    message.success(t('common.done'));
  };

  return (
    <div className="mt-3.5">
      {spaceEditor && membersMaxLimit && (
        <>
          <div className="flex items-center space-x-4">
            <span>
              {t('views.settings.members.space_seats')}: {memberMaxLimitDesc}
            </span>
            <Button onClick={() => checkoutUpgraded(OpenSettingFrom.members)}>
              {t('common.buy_more_seats')}
            </Button>
          </div>
          <div className="flex items-center h-9 mt-2.5 justify-between">
            <div className="text-black text-t2">{t('views.settings.members.invite_members')}</div>
            <div className="flex items-center space-x-4 text-active_color text-t3">
              <div hidden={__PRIVATE__} className="animate-click" onClick={openImportMember}>
                {t('views.settings.members.bulk_import')}
              </div>
              <div className="animate-click" onClick={openExportSpaceMember}>
                {t('views.settings.members.export_members')}
              </div>
            </div>
          </div>
          <div
            className={cx(
              'mt-2 mb-[18px] text-t4 text-grey3',
              !spaceShareInfo?.expirationAt && 'opacity-0'
            )}
          >
            {dayjs(spaceShareInfo?.expirationAt).format(
              toDayjsDateFormat(t('common.date_format_ymd'))
            )}
            {t('views.settings.members.invite_link_expires')}
          </div>
          <Input
            readonly
            inputClassName="h-8 text-black pr-0"
            value={spaceShareLink}
            addonAfter={
              <>
                <span
                  onClick={resetSpareShareLink}
                  className="flex justify-center items-center px-4 h-8 text-center text-black rounded-r-sm border-l border-grey6 animate-click text-t2-medium"
                >
                  {t('common.reset_link')}
                </span>
                <span
                  onClick={clickCopy}
                  className="flex justify-center items-center px-4 h-8 text-center text-black rounded-r-sm border-l border-grey6 animate-click text-t2-medium"
                >
                  {t('common.copy_invite_link')}
                </span>
              </>
            }
          />
          <SettingModalCommon.SettingDivider className="my-2.5" />
        </>
      )}
      <div className="flex top-0 shrink-0 items-center justify-between mt-2.5 h-9 text-t2">
        <div>{t('views.settings.members.user_info')}</div>
        <Input
          className="pl-3 pr-1.5 w-full max-w-[300px] h-8 rounded-full"
          inputClassName="px-0"
          value={searchKeywords}
          onChange={setSearchKeywords}
          placeholder={t('common.search_member_phone')}
          addonBefore={<Icon size="middle" name="IcSearch" className="text-grey4" />}
          addonAfter={
            searchKeywords && (
              <Icon
                size="middle"
                name="IcUploadCancel"
                onClick={() => setSearchKeywords('')}
                className="text-grey4 cursor-pointer"
              />
            )
          }
        />
      </div>
      <FoldItemList
        foldLength={50}
        items={userList}
        renderItemContent={(user) => {
          const userEditorRole = user.uuid === currentUser.uuid || spaceEditor;
          const markName = space.userRemark?.[user.uuid];
          let userName = user.nickname;
          if (markName) {
            userName = `${markName} (${user.nickname || getUnnamedUser()})`;
          }
          return (
            <div className="group flex shrink-0 items-center h-[58px]" key={user.uuid}>
              <UserAvatar user={user} className="mr-2 w-[30px] h-[30px] !text-t1" />
              <div className="flex flex-col grow">
                {editorMarkNameId === user.uuid ? ( // 编辑备注
                  <Input
                    autoFocus={true}
                    maxLength={20}
                    className="pl-3 pr-1.5 w-full max-w-[300px] h-8 rounded-sm"
                    inputClassName="px-0"
                    defaultValue={markName}
                    onChange={(v) => {
                      lastEditMarkNameRef.current = v;
                    }}
                    placeholder={t('views.settings.members.input_remark')}
                    onEnter={(e: React.KeyboardEvent) => {
                      if (e.target instanceof HTMLElement) {
                        e.target.blur();
                      }
                    }}
                    onBlur={() => {
                      const markName = lastEditMarkNameRef.current;
                      lastEditMarkNameRef.current = undefined;
                      setEditorMarkNameId(undefined);
                      if (markName !== undefined) {
                        // 本地更改一下space数据
                        dispatch(
                          ADD_MARK_NAME({
                            uuid: space.uuid,
                            patch: [
                              {
                                userId: user.uuid,
                                markName,
                              },
                            ],
                          })
                        );
                        void request.infra.addSpaceUserRemark(space.uuid, {
                          [user.uuid]: markName,
                        });
                      }
                    }}
                  />
                ) : (
                  <>
                    <div className="flex items-center">
                      <span className="mb-0.5 text-t2">{userName || getUnnamedUser()}</span>
                      {userEditorRole && (
                        <MarkNameEditButton
                          className="ml-1 cursor-pointer transition-opacity group-hover:opacity-100 opacity-0"
                          onClick={() => {
                            setEditorMarkNameId(user.uuid);
                          }}
                        />
                      )}
                    </div>
                    <span className="text-grey3 text-t4">
                      {encryptionPhone(user.phone || user.email)}
                    </span>
                  </>
                )}
              </div>
              <div
                className={cx(
                  'self-center flex items-center text-t2',
                  userEditorRole && 'cursor-pointer'
                )}
                onClick={(e) => userEditorRole && openOption(e, user.uuid)}
              >
                {user.spaceRole === PermissionRole.EDITOR ? t('common.admin') : t('common.member')}
                {userEditorRole && <Icon name="IcArrowDown01" className="ml-1" size="xxxsmall" />}
              </div>
            </div>
          );
        }}
      />
    </div>
  );
};
